import { normaliseUsername, TFunction } from '@psyomics/ui-common';
import { ConfirmForgotPasswordForm, BackLink } from '@psyomics/ui-components';
import { useTranslation } from 'react-i18next';
import { useLocation, Navigate, useNavigate, useParams } from 'react-router-dom';

import { getConfig } from '../../../config';

const createResendHandler =
  ({
    patientApiUrl,
    email,
    t,
    organisation,
  }: {
    patientApiUrl: string;
    email: string;
    t: TFunction;
    organisation?: string;
  }) =>
  async () => {
    try {
      const response = await fetch(`${patientApiUrl}/auth/forgot-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: email,
          ...(organisation && { organisation }),
        }),
      });

      if (!response.ok) {
        throw new Error('resend code failed');
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }

      throw new Error(t('common.errors.networkError'));
    }
  };

const ConfirmForgotPassword = () => {
  const { t } = useTranslation();
  const { patientApiUrl } = getConfig();
  const navigate = useNavigate();
  const { organisation } = useParams();
  const username = useLocation().state?.username;

  if (!username) return <Navigate replace to="/forgot-password" />;

  const handleResendCode = createResendHandler({ patientApiUrl, email: username, t, organisation });

  const handleSubmit = async ({
    confirmationCode,
    password,
    confirmPassword,
  }: {
    confirmationCode: string;
    password: string;
    confirmPassword: string;
  }) => {
    if (password !== confirmPassword) {
      throw new Error(t('auth.common.errors.passwordsDoNotMatch'));
    }

    try {
      const response = await fetch(`${patientApiUrl}/auth/confirm-forgot-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: normaliseUsername(username),
          confirmationCode,
          password,
          ...(organisation && { organisationCode: organisation }),
        }),
      });

      if (!response.ok) {
        throw new Error(t('auth.common.errors.resetPasswordFailed'));
      }

      navigate('/forgot-password/success');
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(t('common.errors.networkError'));
    }
  };

  return (
    <div className="form-container">
      <BackLink text={t('common.buttons.goBack')} />
      <ConfirmForgotPasswordForm email={username} onResendCode={handleResendCode} onSubmit={handleSubmit} />
    </div>
  );
};

export { ConfirmForgotPassword };
